import "assets/scss/components/code.scss";
import React, { useState, useEffect, useRef } from "react";
import { useApp } from "context";
import { Helpers } from "utils";

type Props = {
  name?: string;
  count: number;
  required?: boolean;
  autofocus?: boolean;
  value?: number | string;
  onInput?: (name: string, value: string) => void;
};

export const VerificationCode = (props: Props) => {
  const { theme } = useApp();

  const [value, setValue] = useState<string[]>(
    props.value
      ? String(props.value).slice(0, props.count).split("")
      : Array.from({ length: Number(props.count) }, () => "")
  );
  const [isFocused, setIsFocused] = useState<number>(-1);

  const inputs = useRef<HTMLInputElement[]>([]);

  useEffect(() => {
    if (value.filter((x) => /\S/.test(x)).join("").length === props.count) {
      inputs.current?.[props.count - 1]?.blur();
      props.onInput && props.onInput(props.name ?? "", value.join(""));
    }
  }, [value]);

  const handleFocus = (
    event: React.FocusEvent<HTMLInputElement>,
    index: number
  ) => {
    event.target.select();
    setIsFocused(index);
  };

  const handleInput = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const [first, ...rest] = event.target.value;

    setValue((prevValue) =>
      Helpers.setArrayIndex(prevValue, index, first ?? "")
    );

    if (first) {
      const input = inputs.current?.[index + 1];
      if (input) {
        input.focus();
        input.value = rest.join("");
        input.dispatchEvent(new Event("input", { bubbles: true }));
        setIsFocused(index + 1);
      }
    }
  };

  const handleKeydown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (event.key === "Backspace" && event.currentTarget.value === "") {
      inputs.current?.[Math.max(0, index - 1)]?.focus();
    }
  };

  return (
    <div className="qh-code">
      {Array.from(Array(props.count).keys()).map((_, i: number) => (
        <input
          key={i}
          ref={(input: HTMLInputElement) => (inputs.current[i] = input)}
          onFocus={(e) => handleFocus(e, i)}
          onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleInput(e, i)
          }
          onKeyDown={(e) => handleKeydown(e, i)}
          autoComplete="off"
          autoFocus={i === 0}
          value={value[i]}
          required
          style={{
            color: theme.palette.font.color,
            backgroundColor: theme.palette.background.color,
            outlineColor: isFocused === i ? theme.color.primary.color : "",
          }}
        />
      ))}
    </div>
  );
};
